/**
 * DeliveryImportModal - 3-step import flow
 *
 * Step 1: Upload / paste file
 * Step 2: Map CSV columns → AEGIS fields
 * Step 3: Import + success
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  X, Upload, FileText, AlertCircle, CheckCircle2,
  ArrowRight, ArrowLeft, ChevronDown, Info
} from 'lucide-react';
import api from '../../../lib/api';

/* ── AEGIS target fields ─────────────────────────────────────────────────── */
export const TARGET_FIELDS = [
  { key: 'shipment_date',        label: 'Date / Shipment Date',      type: 'date',    required: false, hint: 'e.g. 2024-03-15' },
  { key: 'shipment_no',          label: 'Shipment Number',           type: 'text',    required: false, hint: 'e.g. SHIP-001' },
  { key: 'delivery_no',          label: 'Delivery Number',           type: 'text',    required: true,  hint: 'e.g. DEL-001' },
  { key: 'distribution_channel', label: 'Distribution Channel',      type: 'text',    required: false, hint: 'e.g. Direct, Retail' },
  { key: 'sales_order_no',       label: 'Sales Order Number',        type: 'text',    required: false, hint: 'e.g. SO-12345' },
  { key: 'carrier_name',         label: 'Carrier Name',              type: 'text',    required: false, hint: 'e.g. FedEx Freight' },
  { key: 'trailer_no',           label: 'Trailer Number',            type: 'text',    required: false, hint: 'e.g. TRL-789' },
  { key: 'customer_name',        label: 'Customer Name',             type: 'text',    required: false, hint: 'e.g. Acme Corp' },
  { key: 'customer_address',     label: 'Customer Address',          type: 'text',    required: false, hint: 'e.g. 123 Main St, City' },
  { key: 'material_desc',        label: 'Material / Part Description', type: 'text',  required: false, hint: 'e.g. Steel Beams' },
  { key: 'category',             label: 'Category',                  type: 'text',    required: false, hint: 'e.g. Construction' },
  { key: 'item_no',              label: 'Item Number',               type: 'integer', required: false, hint: 'Numeric item ID' },
  { key: 'quantity',             label: 'Quantity',                   type: 'integer', required: true,  hint: 'Quantity — used to generate labels' },
  { key: 'stop_no',              label: 'Stop Number',               type: 'integer', required: false, hint: 'Delivery stop sequence' },
];

/* ── Auto-detect best match for a CSV column ─────────────────────────────── */
const AUTO_MATCH_RULES = {
  shipment_date:        ['date', 'shipmentdate', 'shipment_date', 'datetime'],
  shipment_no:          ['shipmentno', 'shipment_no', 'shipmentnumber', 'shipment_number'],
  delivery_no:          ['deliveryno', 'delivery_no', 'deliverynumber', 'delivery_number', 'deliveryid'],
  distribution_channel: ['distributionchannel', 'distribution_channel', 'channel', 'dist_channel'],
  sales_order_no:       ['salesorderno', 'sales_order_no', 'salesorder', 'sales_order', 'orderno', 'order_no'],
  carrier_name:         ['carriername', 'carrier_name', 'carrier'],
  trailer_no:           ['trailerno', 'trailer_no', 'trailer', 'trailernumber'],
  customer_name:        ['customername', 'customer_name', 'customer', 'clientname', 'client_name'],
  customer_address:     ['customeraddress', 'customer_address', 'address', 'deliveryaddress', 'delivery_address'],
  material_desc:        ['materialdesc', 'material_desc', 'material', 'description', 'partdesc', 'part_desc', 'partdescription', 'part'],
  category:             ['category', 'cat', 'type'],
  item_no:              ['itemno', 'item_no', 'itemnumber', 'item_number', 'itemid', 'item_id'],
  quantity:             ['quantity', 'qty', 'boxquantity', 'box_quantity', 'boxqty', 'box_qty', 'boxes', 'totalboxes', 'total_boxes', 'itemquantity', 'item_quantity', 'itemqty', 'item_qty'],
  stop_no:              ['stopno', 'stop_no', 'stop', 'stopnumber', 'stop_number'],
};

function normalizeForMatch(str) {
  return str.toLowerCase().replace(/[\s_\-#]/g, '');
}

function autoDetectMapping(csvHeaders) {
  const mapping = {};
  csvHeaders.forEach(header => {
    const normalized = normalizeForMatch(header);
    for (const [fieldKey, aliases] of Object.entries(AUTO_MATCH_RULES)) {
      if (aliases.includes(normalized)) {
        mapping[header] = fieldKey;
        break;
      }
    }
    if (!mapping[header]) mapping[header] = ''; // no match
  });
  return mapping;
}

/* ── CSV / JSON parsers ──────────────────────────────────────────────────── */
/**
 * Properly parse CSV line handling quoted fields and commas within quotes
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last field
  result.push(current.trim());
  return result;
}

/**
 * Check if a value looks like a city/state/zip pattern (e.g., "Huntsville AL 35806")
 */
function looksLikeCityStateZip(value) {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  
  // Pattern: City Name, State Code, ZIP (e.g., "Huntsville AL 35806" or "Fort Payne AL 35967")
  // Must have: letters (city), 2 uppercase letters (state), 5 digits (zip)
  // More lenient patterns to catch various formats:
  const patterns = [
    /^[A-Za-z\s]+,\s*[A-Z]{2}\s+\d{5}/,           // "City, ST 12345" or " City, ST 12345"
    /^[A-Za-z\s]+\s+[A-Z]{2}\s+\d{5}/,            // "City ST 12345" or " City ST 12345" or " Huntsville AL 35806"
    /^[A-Z][a-z]+(\s+[A-Z][a-z]+)*\s+[A-Z]{2}\s+\d{5}/, // "Huntsville AL 35806"
    /\s+[A-Z]{2}\s+\d{5}$/,                       // Ends with " ST 12345" (catches leading spaces)
  ];
  
  // Also check if it matches the basic structure: has state code (2 uppercase) and zip (5 digits)
  const hasStateZip = /\b[A-Z]{2}\s+\d{5}\b/.test(trimmed);
  const hasCityLikeText = /[A-Za-z]{3,}/.test(trimmed); // Has at least 3 letters (city name)
  
  return patterns.some(pattern => pattern.test(trimmed)) || (hasStateZip && hasCityLikeText);
}

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  
  // Parse headers
  const headerValues = parseCSVLine(lines[0]);
  const headers = headerValues.map(h => h.replace(/^"|"$/g, ''));
  const expectedColumnCount = headers.length;
  
  // Find the DeliveryAddress column index
  const addressColumnIndex = headers.findIndex(h => 
    /deliveryaddress|delivery_address|address/i.test(h)
  );
  
  // Parse rows
  const rows = [];
  const errors = [];
  let autoFixedCount = 0;
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = parseCSVLine(line);
    
    // If we have exactly one extra column and we found an address column, try to merge
    if (values.length === expectedColumnCount + 1 && addressColumnIndex >= 0) {
      const addressIndex = addressColumnIndex;
      const nextIndex = addressIndex + 1;
      
      if (nextIndex < values.length) {
        const nextValue = values[nextIndex];
        const trimmedNext = nextValue?.trim() || '';
        
        // Check what the next expected field should be (after address in headers)
        const nextHeaderIndex = addressColumnIndex + 1;
        const nextHeaderName = nextHeaderIndex < headers.length ? headers[nextHeaderIndex]?.toLowerCase() || '' : '';
        const isNextFieldDate = /date|time|shipping/.test(nextHeaderName);
        const isNextFieldCompany = /client|company|customer|name/.test(nextHeaderName);
        
        // Check if the next column looks like a city/state/zip (part of the address)
        const looksLikeAddress = looksLikeCityStateZip(nextValue);
        
        // Check if nextValue looks like what the next expected field should be
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}/.test(trimmedNext); // ISO date
        const looksLikeCompanyName = trimmedNext.length > 15 && !looksLikeDate && !/^\d/.test(trimmedNext) && 
                                     !looksLikeAddress && /[A-Z][a-z]/.test(trimmedNext); // Has capital letters like company names
        
        // More aggressive: if next field expects a company name but this looks like address part, merge it
        // Or if it clearly looks like address part (city/state/zip pattern)
        // Only skip merging if it clearly looks like the expected next field
        const shouldSkipMerge = (isNextFieldCompany && looksLikeCompanyName) || 
                                (isNextFieldDate && looksLikeDate);
        
        // Merge if it looks like address OR if it doesn't look like the expected next field
        if (looksLikeAddress || (!shouldSkipMerge && trimmedNext.length < 50)) {
          // Merge the address columns
          const mergedAddress = `${values[addressIndex]}, ${trimmedNext}`;
          values[addressIndex] = mergedAddress;
          values.splice(nextIndex, 1); // Remove the extra column
          autoFixedCount++;
        }
      }
    }
    
    // Validate column count after potential fix
    if (values.length !== expectedColumnCount) {
      errors.push({
        line: i + 1,
        expected: expectedColumnCount,
        actual: values.length,
        preview: line.substring(0, 100)
      });
      // Still try to map what we have, padding with empty strings if needed
      while (values.length < expectedColumnCount) {
        values.push('');
      }
      // Truncate if too many
      if (values.length > expectedColumnCount) {
        values.splice(expectedColumnCount);
      }
    }
    
    const row = {};
    headers.forEach((h, j) => { 
      const val = values[j] ?? '';
      row[h] = val.replace(/^"|"$/g, ''); // Remove surrounding quotes
    });
    rows.push(row);
  }
  
  // If we detected column count mismatches, this might indicate unquoted commas in fields
  // The parser will still work, but the data might be misaligned
  if (errors.length > 0) {
    console.warn('CSV parsing warnings:', errors);
  }
  
  if (autoFixedCount > 0) {
    console.log(`Auto-fixed ${autoFixedCount} rows by merging split address columns`);
  }
  
  return { headers, rows, _parseErrors: errors, _autoFixed: autoFixedCount };
}

function parseJSON(text) {
  const parsed = JSON.parse(text);
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  return { headers, rows };
}

/* ── Apply mapping to raw rows ───────────────────────────────────────────── */
function applyMapping(rawRows, columnMapping) {
  return rawRows.map(row => {
    const mapped = { _raw: row };
    Object.entries(columnMapping).forEach(([csvCol, fieldKey]) => {
      if (fieldKey && row[csvCol] !== undefined) {
        const val = row[csvCol];
        mapped[fieldKey] = val === '' ? null : val;
      }
    });
    return mapped;
  });
}

/* ── Step indicator ──────────────────────────────────────────────────────── */
function StepIndicator({ step, isDark }) {
  const steps = ['Upload', 'Map Columns', 'Import'];
  return (
    <div className="flex items-center gap-2">
      {steps.map((label, i) => {
        const num = i + 1;
        const isActive = step === num;
        const isDone = step > num;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                isDone  ? 'bg-green-500 text-white' :
                isActive ? 'bg-violet-600 text-white' :
                isDark  ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
              }`}>
                {isDone ? '✓' : num}
              </div>
              <span className={`text-xs font-medium ${
                isActive ? (isDark ? 'text-white' : 'text-gray-900') :
                isDone   ? 'text-green-400' :
                isDark   ? 'text-gray-500' : 'text-gray-400'
              }`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} style={{ minWidth: 20 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Main Modal ──────────────────────────────────────────────────────────── */
export default function DeliveryImportModal({ isDark, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState('');
  const [parseError, setParseError] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({}); // { csvColumn: fieldKey }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef();

  /* ── Parse file/text → extract headers + rows ── */
  const processText = useCallback((text, fileName = '') => {
    setParseError('');
    try {
      let result;
      if (fileName.endsWith('.json') || text.trim().startsWith('[') || text.trim().startsWith('{')) {
        result = parseJSON(text);
      } else {
        result = parseCSV(text);
      }
      if (!result.rows.length) { setParseError('No data rows found in file.'); return false; }
      
      // Check for parsing errors (column count mismatches)
      if (result._parseErrors && result._parseErrors.length > 0) {
        const errorCount = result._parseErrors.length;
        const autoFixed = result._autoFixed || 0;
        
        if (autoFixed > 0 && autoFixed === errorCount) {
          // All errors were auto-fixed
          const warningMsg = `Warning: Detected ${errorCount} row${errorCount !== 1 ? 's' : ''} with split address fields (due to unquoted commas). ` +
            `Automatically merged address columns. ` +
            `For best results, quote address fields in your CSV, e.g., "123 Main St, City, State".`;
          setParseError(warningMsg);
        } else if (errorCount > 0) {
          // Some errors couldn't be auto-fixed
          const warningMsg = `Warning: ${errorCount} row${errorCount !== 1 ? 's' : ''} have incorrect column counts. ` +
            `${autoFixed > 0 ? `${autoFixed} row${autoFixed !== 1 ? 's' : ''} were auto-fixed, but ` : ''}` +
            `some data may still be misaligned. ` +
            `Please ensure fields with commas (like addresses) are wrapped in quotes, e.g., "123 Main St, City, State".`;
          setParseError(warningMsg);
        }
      }
      
      setCsvHeaders(result.headers);
      setRawRows(result.rows);
      setColumnMapping(autoDetectMapping(result.headers));
      return true;
    } catch (err) {
      setParseError(`Parse error: ${err.message}`);
      return false;
    }
  }, []);

  const handleFile = (f) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setRawText(text);
      processText(text, f.name);
    };
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handlePasteChange = (e) => {
    const text = e.target.value;
    setRawText(text);
    if (text.trim()) processText(text);
    else { setCsvHeaders([]); setRawRows([]); setParseError(''); }
  };

  const handleNext = () => {
    if (step === 1 && rawRows.length > 0) setStep(2);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
  };

  const handleImport = async () => {
    const mappedRecords = applyMapping(rawRows, columnMapping);
    setImporting(true);
    try {
      const result = await api.post('/api/delivery/shipments/import', {
        records: mappedRecords,
        fileName: file?.name || 'paste',
      });
      setImportResult(result);
      setStep(3);
    } catch (err) {
      setParseError(`Import failed: ${err.message || 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  /* ── Mapping stats ── */
  const mappedCount = useMemo(
    () => Object.values(columnMapping).filter(v => v !== '').length,
    [columnMapping]
  );
  const requiredMapped = useMemo(
    () => TARGET_FIELDS.filter(f => f.required).every(f =>
      Object.values(columnMapping).includes(f.key)
    ),
    [columnMapping]
  );

  /* ── Preview of first 3 rows with current mapping ── */
  const previewMapped = useMemo(() => {
    if (!rawRows.length) return [];
    return applyMapping(rawRows.slice(0, 3), columnMapping);
  }, [rawRows, columnMapping]);

  const overlay = 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4';
  const modal = `relative w-full rounded-2xl border shadow-2xl flex flex-col ${
    step === 2 ? 'max-w-4xl' : 'max-w-2xl'
  } ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className={overlay}>
      <div className={modal} style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex-1">
            <h2 className={`text-lg font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Shipment Data</h2>
            <StepIndicator step={step} isDark={isDark} />
          </div>
          <button
            onClick={onClose}
            className={`ml-4 p-1.5 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── STEP 1: Upload ── */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  dragging
                    ? 'border-violet-500 bg-violet-500/10'
                    : isDark ? 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/40' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.json,.txt"
                  className="hidden"
                  onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                />
                <Upload size={28} className={`mx-auto mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                {file ? (
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    <FileText size={14} className="inline mr-1" />{file.name}
                    {rawRows.length > 0 && (
                      <span className="ml-2 text-green-400">— {rawRows.length} rows detected</span>
                    )}
                  </p>
                ) : (
                  <>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Drop CSV or JSON file here
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      or click to browse · Any column names accepted
                    </p>
                  </>
                )}
              </div>

              {/* Paste area */}
              <div>
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>— or paste data —</p>
                <textarea
                  value={rawText}
                  onChange={handlePasteChange}
                  placeholder="Paste CSV or JSON data here..."
                  rows={4}
                  className={`w-full px-3 py-2 rounded-lg border text-xs font-mono outline-none resize-none ${
                    isDark
                      ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-gray-400'
                  }`}
                />
              </div>

              {parseError && (
                <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${
                  parseError.startsWith('Warning:')
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <AlertCircle size={15} className={`flex-shrink-0 mt-0.5 ${
                    parseError.startsWith('Warning:')
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`} />
                  <p className={`text-xs ${
                    parseError.startsWith('Warning:')
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`}>{parseError}</p>
                </div>
              )}

              {/* Raw preview */}
              {rawRows.length > 0 && (
                <div>
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Raw preview — {rawRows.length} rows · {csvHeaders.length} columns detected
                  </p>
                  <div className={`rounded-lg border overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                          {csvHeaders.slice(0, 6).map(h => (
                            <th key={h} className={`px-3 py-2 text-left font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {h}
                            </th>
                          ))}
                          {csvHeaders.length > 6 && (
                            <th className={`px-3 py-2 text-left font-medium ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                              +{csvHeaders.length - 6} more
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {rawRows.slice(0, 3).map((row, i) => (
                          <tr key={i} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                            {csvHeaders.slice(0, 6).map(h => (
                              <td key={h} className={`px-3 py-2 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {String(row[h] ?? '—').slice(0, 25)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Map Columns ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-700/40' : 'bg-blue-50 border-blue-200'}`}>
                <Info size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <p className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  We've auto-detected <strong>{mappedCount}</strong> of <strong>{csvHeaders.length}</strong> columns.
                  Review the mappings below and adjust any that don't look right.
                  Columns set to <em>"— Skip —"</em> will be ignored.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {csvHeaders.map(csvCol => {
                  const currentMapping = columnMapping[csvCol] || '';
                  const sampleValues = rawRows.slice(0, 3).map(r => r[csvCol]).filter(Boolean);
                  const isAutoMatched = currentMapping !== '';
                  const targetField = TARGET_FIELDS.find(f => f.key === currentMapping);

                  return (
                    <div
                      key={csvCol}
                      className={`rounded-lg border p-3 ${
                        isAutoMatched
                          ? isDark ? 'border-violet-700/50 bg-violet-900/10' : 'border-violet-200 bg-violet-50/50'
                          : isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      {/* CSV column name */}
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className={`text-xs font-bold font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {csvCol}
                          </span>
                          {isAutoMatched && (
                            <span className="ml-2 text-xs text-violet-400">auto-matched</span>
                          )}
                        </div>
                        {targetField?.required && (
                          <span className="text-xs text-red-400 font-medium">required</span>
                        )}
                      </div>

                      {/* Sample values */}
                      {sampleValues.length > 0 && (
                        <div className={`text-xs mb-2 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          e.g. {sampleValues.slice(0, 2).join(', ')}
                        </div>
                      )}

                      {/* Arrow + dropdown */}
                      <div className="flex items-center gap-2">
                        <ArrowRight size={12} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
                        <div className="relative flex-1">
                          <select
                            value={currentMapping}
                            onChange={e => setColumnMapping(prev => ({ ...prev, [csvCol]: e.target.value }))}
                            className={`w-full appearance-none pl-3 pr-7 py-1.5 rounded-lg border text-xs font-medium outline-none transition-colors ${
                              currentMapping
                                ? isDark ? 'bg-violet-900/30 border-violet-600 text-violet-300' : 'bg-violet-50 border-violet-300 text-violet-700'
                                : isDark ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-white border-gray-300 text-gray-500'
                            }`}
                          >
                            <option value="">— Skip —</option>
                            {TARGET_FIELDS.map(f => (
                              <option key={f.key} value={f.key}>
                                {f.label}{f.required ? ' *' : ''}
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                      </div>

                      {/* Field hint */}
                      {targetField && (
                        <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                          {targetField.hint}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Required fields warning */}
              {!requiredMapped && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-400">
                    Required fields not yet mapped:{' '}
                    {TARGET_FIELDS.filter(f => f.required && !Object.values(columnMapping).includes(f.key))
                      .map(f => f.label).join(', ')}
                  </p>
                </div>
              )}

              {parseError && (
                <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${
                  parseError.startsWith('Warning:')
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <AlertCircle size={15} className={`flex-shrink-0 mt-0.5 ${
                    parseError.startsWith('Warning:')
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`} />
                  <p className={`text-xs ${
                    parseError.startsWith('Warning:')
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`}>{parseError}</p>
                </div>
              )}

              {/* Mapped preview */}
              {previewMapped.length > 0 && (
                <div>
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Preview after mapping (first 3 rows)
                  </p>
                  <div className={`rounded-lg border overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                          {TARGET_FIELDS.filter(f =>
                            Object.values(columnMapping).includes(f.key)
                          ).slice(0, 6).map(f => (
                            <th key={f.key} className={`px-3 py-2 text-left font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {f.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewMapped.map((row, i) => (
                          <tr key={i} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                            {TARGET_FIELDS.filter(f =>
                              Object.values(columnMapping).includes(f.key)
                            ).slice(0, 6).map(f => (
                              <td key={f.key} className={`px-3 py-2 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {String(row[f.key] ?? '—').slice(0, 25)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Success ── */}
          {step === 3 && importResult && (
            <div className="text-center py-8 space-y-4">
              <CheckCircle2 size={52} className="text-green-400 mx-auto" />
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Complete!</h3>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{importResult.success}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Imported</div>
                </div>
                {importResult.failed > 0 && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-400">{importResult.failed}</div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Failed</div>
                  </div>
                )}
                <div className="text-center">
                  <div className={`text-3xl font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{importResult.total}</div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</div>
                </div>
              </div>
              {importResult.failed > 0 && (
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {importResult.failed} row{importResult.failed !== 1 ? 's' : ''} could not be imported. Check that required fields are mapped correctly.
                </p>
              )}
              <button
                onClick={onImported}
                className="px-8 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors"
              >
                View Shipments
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 3 && (
          <div className={`flex items-center justify-between px-6 py-4 border-t flex-shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div>
              {step === 2 && (
                <button
                  onClick={handleBack}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
              {step === 1 && (
                <button
                  onClick={onClose}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {step === 1 && rawRows.length > 0 && (
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {rawRows.length} rows · {csvHeaders.length} columns
                </span>
              )}
              {step === 2 && (
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {mappedCount} of {csvHeaders.length} columns mapped
                </span>
              )}

              {step === 1 && (
                <button
                  onClick={handleNext}
                  disabled={!rawRows.length}
                  className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white rounded-lg font-medium transition-colors text-sm"
                >
                  Next: Map Columns <ArrowRight size={14} />
                </button>
              )}

              {step === 2 && (
                <button
                  onClick={handleImport}
                  disabled={importing || !requiredMapped}
                  className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white rounded-lg font-medium transition-colors text-sm"
                >
                  {importing ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload size={14} />
                  )}
                  Import {rawRows.length} Shipment{rawRows.length !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
